import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { NzTreeNodeOptions } from "ng-zorro-antd/tree";
import { take } from "rxjs/operators";
import { IGoal } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { OKRsEventType } from "@gtmhub/okrs/events";
import { USER_ROLES_VIEWONLY } from "@gtmhub/shared/utils/user-roles";
import { CurrentUserService } from "@gtmhub/users";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import {
  CascadingKRLinks,
  ExpandedLinkType,
  ILinksSpecDetail,
  Link,
  LinkSourceTargetType,
  LinkSpecDetail,
  Links,
  LinksDTO,
  RelatedLink,
  SourceType,
} from "@webapp/links/models/links.models";
import { IRelatedItem, IRelatedItemsOnChanges, IRelatedItemsSelectorEmission, IUpdateLinkArgs } from "@webapp/links/models/related-items.models";
import { LinksFacade } from "@webapp/links/services/links-facade.service";
import { generateLink, generateRelation, generateSpec } from "@webapp/links/utils/related-items.utils";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { RelatedItemRowComponent } from "../related-item-row/related-item-row.component";

const defaultLimit = 5;

@Component({
  selector: "related-items",
  templateUrl: "./related-items.component.html",
  styleUrls: ["./related-items.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelatedItemsComponent implements OnInit, OnChanges {
  @Input() public links: Links<LinkSpecDetail>;
  @Input() public linkSourceTarget: Goal | Metric;
  @Input() public linkSourceTargetId: string;
  @Input() public linkSourceTargetType: LinkSourceTargetType;

  @ViewChild("addItemBtn", { read: ElementRef }) public addItemBtn: ElementRef<HTMLElement>;
  @ViewChildren(RelatedItemRowComponent) public relatedItemRowComponents: QueryList<RelatedItemRowComponent>;

  public relatedItems: IRelatedItem[] = [];
  public relationsToExclude: Set<RelatedLink>;
  public treeNodes: NzTreeNodeOptions[] = [];
  public tooltipTitle: string;
  public hasViewOnlyRole: boolean;
  public showSelector = false;
  public defaultLimit = defaultLimit;
  public limit = defaultLimit;
  public showAllBtnLabel: string;

  private defaultLink: Link = "relating";
  private sourceType: SourceType;
  private recentlySelected: { [key: string]: IRelatedItemsSelectorEmission } = {};

  constructor(private linksFacade: LinksFacade, private broadcastService: BroadcastService, private currentUserService: CurrentUserService) {
    this.relationsToExclude = new Set([CascadingKRLinks.CASCADING, CascadingKRLinks.CONTRIBUTING]);
  }

  public ngOnInit(): void {
    const sortedRelatedItems = this.getLinksRelatedItems(this.links).sort((a, b) => b.dateCreated.localeCompare(a.dateCreated));
    this.relatedItems = this.excludeRelations(sortedRelatedItems);
    this.sourceType = this.getSourceType();
    this.hasViewOnlyRole = this.currentUserService.userHasRole(USER_ROLES_VIEWONLY);
    this.tooltipTitle = localize("insufficient_permissions_to_manage_related_items");
    this.setShowAllButtonLabel();
  }

  public ngOnChanges(changes: IRelatedItemsOnChanges): void {
    if (changes.links && !changes.links.firstChange) {
      const prevRelatedItems = this.relatedItems;
      const sortedRelatedItems = this.getLinksRelatedItems(changes.links.currentValue).sort((a, b) => b.dateCreated.localeCompare(a.dateCreated));
      this.relatedItems = this.excludeRelations(sortedRelatedItems);
      this.setShowAllButtonLabel();

      if (prevRelatedItems.length > this.relatedItems.length) {
        this.setFocusOnItemDeletion(prevRelatedItems, this.relatedItems);
      } else if (prevRelatedItems.length < this.relatedItems.length) {
        this.focusRelatedItemRow(0, "status");
      }
    }

    if (this.limit > defaultLimit) {
      this.limit = this.relatedItems.length;
    }
  }

  private setFocusOnItemDeletion(prevRelatedItems: IRelatedItem[], currRelatedItems: IRelatedItem[]): void {
    const prevItemIndex = prevRelatedItems.findIndex((item, i) => item.id !== currRelatedItems[i]?.id);
    const index = prevItemIndex > 0 ? prevItemIndex - 1 : 0;

    if (currRelatedItems.length) {
      this.focusRelatedItemRow(index, "info");
    } else {
      this.addItemBtn?.nativeElement.focus();
    }
  }

  private excludeRelations(relatedItems: IRelatedItem[]): IRelatedItem[] {
    return relatedItems.filter((item) => !this.relationsToExclude.has(item.linkType));
  }

  private getSourceType(): SourceType {
    return this.linkSourceTargetType === "goals" ? "goal" : "metric";
  }

  public addRelatedItem(): void {
    this.showSelector = true;
  }

  public onRelatedItemChange(item: IRelatedItemsSelectorEmission): void {
    this.recentlySelected[item.id] = item;
    this.createLink(item);
  }

  private createLink(suggestedItem: IRelatedItemsSelectorEmission): void {
    if (!suggestedItem.id) {
      return;
    }

    this.showSelector = false;
    const newLink: LinksDTO<Link> = {
      linkType: this.defaultLink,
      sourceId: this.linkSourceTargetId,
      sourceType: this.linkSourceTargetType,
      targetId: suggestedItem.id,
      targetType: suggestedItem.type === "goal" ? "goals" : "metrics",
    };
    this.linksFacade
      .createLink$(newLink)
      .pipe(take(1))
      .subscribe(() => {
        this.treeNodes = [];

        const args = { reason: this.sourceType + "Changed", [this.sourceType + "Id"]: this.linkSourceTargetId, newLink };
        this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { ...args });
      });
  }

  public deleteLink(relatedItem: IRelatedItem): void {
    const link = generateLink(this.linkSourceTargetId, this.linkSourceTargetType, relatedItem);

    const subscribe = this.linksFacade.deleteLink$(link).subscribe(() => {
      const args = { reason: this.sourceType + "Changed", [this.sourceType + "Id"]: this.linkSourceTargetId, link };

      this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { ...args });
      subscribe.unsubscribe();
    });
  }

  public updateLink(args: IUpdateLinkArgs): void {
    const { sourceId, sourceType } = generateLink(this.linkSourceTargetId, this.linkSourceTargetType, args.relatedItem);

    const relation = generateRelation(args.relatedItem.id, args.relatedItem.iconType);
    const linksToCreate: Links<LinkSpecDetail> = generateSpec(args.relatedItem.linkType, relation);
    const linksToDelete: Links<LinkSpecDetail> = generateSpec(args.linkTypeToDelete, relation);

    const subscribe = this.linksFacade.patchBulkLink$({ sourceId, sourceType }, { linksToCreate, linksToDelete }).subscribe(() => {
      subscribe.unsubscribe();
    });
  }

  private getLinksRelatedItems(links: Links<LinkSpecDetail>): IRelatedItem[] {
    if (!links) {
      return [];
    }

    const relatedItems: IRelatedItem[] = [];

    Object.keys(links.spec).forEach((linkType) => {
      links.spec[linkType].forEach((linkSpec) => {
        const itemDetails = this.getRelatedItemDetails(links, linkSpec);

        if (!itemDetails.name || !itemDetails.ownerIds) {
          return;
        }

        const relatedItem: IRelatedItem = {
          linkType: linkType as RelatedLink,
          iconType: linkSpec.type === "related" ? "relating" : linkSpec.type,
          id: linkSpec.id,
          dateCreated: linkSpec.dateCreated,
          name: itemDetails.name,
          ownerIds: itemDetails.ownerIds,
        };
        relatedItems.push(relatedItem);
      });
    });

    return relatedItems;
  }

  private getRelatedItemDetails(links: Links<LinkSpecDetail>, linkSpec: ILinksSpecDetail<LinkSpecDetail>): { ownerIds: string[]; name: string } {
    const type: LinkSourceTargetType = linkSpec.type === "goal" ? ExpandedLinkType.GOALS : ExpandedLinkType.METRICS;
    let item = (links.expanded?.[type] as Array<IGoal | Metric>)?.find((item) => {
      return item.id === linkSpec.id;
    });

    if (!item) {
      item = this.recentlySelected[linkSpec.id];
    } else {
      const hasPermissionToRead = !this.isPrivateKR(item) && this.currentUserService.allowedActionsSync(item.access).includes("read");
      item = hasPermissionToRead ? item : null;
    }

    return { name: item?.name, ownerIds: item?.ownerIds };
  }

  private isPrivateKR(item: IGoal | Metric): boolean {
    return !item.access;
  }

  public setLimit(): void {
    const indexOfRowToFocus = this.limit;
    this.limit = this.relatedItems.length;
    this.focusRelatedItemRow(indexOfRowToFocus, "status");
  }

  public resetLimit(): void {
    this.limit = defaultLimit;
    this.focusRelatedItemRow(this.limit - 1, "info");
  }

  private focusRelatedItemRow(index: number, element: "status" | "info"): void {
    setTimeout(() => {
      if (element === "status") {
        this.relatedItemRowComponents.toArray()[index].relationshipButton.buttonSelect.focusButtonElement();
      } else {
        this.relatedItemRowComponents.toArray()[index].rowInfo.nativeElement.focus();
      }
    }, 100);
  }

  private setShowAllButtonLabel(): void {
    this.showAllBtnLabel = `${localize("show_all")} ${this.relatedItems.length} ${localize("related_items")}`;
  }
}
